import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Order, Product } from "@apis/index";
import { toast } from "react-toastify";
import { Search, Plus, Trash2, Send, Image as ImageIcon } from "lucide-react";
import Column from "@components/column";
import Row from "@components/row";
import Typography from "@components/typography";
import Button from "@components/button";
import Fields from "@components/fields";
import Table from "@components/table";
import { Helper } from "@utils/index";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import useConfig from "@hooks/useConfig";
import * as S from "./style";

const PharmacyPOS = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { bucketUrl } = useConfig();
  const { settings } = useGeneralSettings();

  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [cart, setCart] = useState<any[]>([]);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [discountType, setDiscountType] = useState<"flat" | "percentage">(
    "flat",
  );
  const [discountValue, setDiscountValue] = useState("");

  const [customItem, setCustomItem] = useState({
    name: "",
    price: "",
    unit: "pcs",
    type: "medicine",
    isAlternative: false,
    image: null as File | null,
  });

  const fetchOrder = useCallback(async () => {
    if (!orderId) return;
    try {
      setLoading(true);
      const res = await Order.find(orderId);
      setOrder(res);
      // Initialize cart if order already has items
      if (res.items) {
        setCart(
          res.items.map((item: any) => ({
            ...item.product,
            quantity: item.quantity,
            actualPrice: item.actualAmount / item.quantity,
            isCustom: false,
          })),
        );
      }
    } catch (err) {
      toast.error("Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await Product.list({ params: { search: query, limit: 10 } });
      setSearchResults(res.data || []);
    } catch (err) {
      console.error("Search failed", err);
    }
  };

  const addToCart = (product: any) => {
    const existing = cart.find(
      (item) => item.id === product.id && !item.isCustom,
    );
    if (existing) {
      setCart(
        cart.map((item) =>
          item.id === product.id && !item.isCustom
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        ),
      );
    } else {
      setCart([
        ...cart,
        {
          ...product,
          quantity: 1,
          actualPrice: product.price,
          isCustom: false,
        },
      ]);
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  const addCustomToCart = () => {
    if (!customItem.name || !customItem.price) {
      toast.warn("Please enter name and price");
      return;
    }
    const newItem = {
      ...customItem,
      id: `custom-${Date.now()}`,
      quantity: 1,
      actualPrice: Number(customItem.price),
      isCustom: true,
      price: Number(customItem.price),
    };
    setCart([...cart, newItem]);
    setShowCustomModal(false);
    setCustomItem({
      name: "",
      price: "",
      unit: "pcs",
      type: "medicine",
      isAlternative: false,
      image: null,
    });
  };

  const removeFromCart = (id: string | number) => {
    setCart(cart.filter((item) => item.id !== id));
  };

  const updateQuantity = (id: string | number, delta: number) => {
    setCart(
      cart.map((item) =>
        item.id === id
          ? { ...item, quantity: Math.max(1, item.quantity + delta) }
          : item,
      ),
    );
  };

  const calculateTotal = () => {
    return cart.reduce(
      (sum, item) => sum + item.actualPrice * item.quantity,
      0,
    );
  };

  const calculateDiscount = () => {
    const subtotal = calculateTotal();
    const numericDiscount = Math.max(0, Number(discountValue) || 0);
    return discountType === "percentage"
      ? Math.min(subtotal, (subtotal * Math.min(numericDiscount, 100)) / 100)
      : Math.min(subtotal, numericDiscount);
  };

  const handleSendInvoice = async () => {
    if (cart.length === 0) {
      toast.warn("Cart is empty");
      return;
    }
    try {
      const payload = {
        items: cart.map((item) => ({
          productId: item.isCustom ? null : item.id,
          customName: item.isCustom ? item.name : undefined,
          quantity: item.quantity,
          price: item.actualPrice,
          isCustom: item.isCustom,
          customDetails: item.isCustom
            ? {
                name: item.name,
                unit: item.unit,
                type: item.type,
                isAlternative: item.isAlternative,
              }
            : null,
        })),
        customDiscount: {
          type: discountType,
          value: Math.max(0, Number(discountValue) || 0),
        },
      };

      console.log("POS: Sending Invoice Payload:", payload);
      await Order.sendInvoice(orderId!, payload);
      toast.success("Invoice sent to customer!");
      const prefix = window.location.pathname.startsWith("/admin")
        ? "/admin"
        : "/store";
      navigate(`${prefix}/orders/details/${orderId}`);
    } catch (err) {
      toast.error("Failed to send invoice");
    }
  };

  if (loading) return <div>Loading POS...</div>;

  return (
    <S.POSContainer>
      <S.Header>
        <Typography styleText="font-size:24px;font-weight:600">
          Pharmacy POS
        </Typography>
        <Typography styleText="color:#6B7280">
          Order #{order?.id} - {order?.customer?.name}
        </Typography>
      </S.Header>

      <Row gap="20px" styleRow="align-items: flex-start">
        {/* Left Side: Product Selection */}
        <S.MainContent>
          <S.Card>
            <S.SearchWrapper>
              <Search size={20} color="#9CA3AF" />
              <input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
              <Button
                onClick={() => setShowCustomModal(true)}
                variant="outline"
              >
                <Plus size={18} /> Add Custom
              </Button>
            </S.SearchWrapper>

            {searchResults.length > 0 && (
              <S.SearchResults>
                {searchResults.map((p) => (
                  <S.SearchResultItem key={p.id} onClick={() => addToCart(p)}>
                    <img
                      src={
                        p.images?.[0]?.image
                          ? `${bucketUrl}/${p.images[0].image}`
                          : ""
                      }
                      alt=""
                    />
                    <div>
                      <Typography styleText="font-weight:500">
                        {p.name}
                      </Typography>
                      <Typography styleText="font-size:12px;color:#6B7280">
                        रू {p.price} / {p.unit}
                      </Typography>
                    </div>
                    <Plus size={18} color="#10B981" />
                  </S.SearchResultItem>
                ))}
              </S.SearchResults>
            )}

            <S.PrescriptionView>
              <Typography styleText="font-weight:600;margin-bottom:10px">
                Uploaded Prescriptions
              </Typography>
              <S.PrescriptionGrid>
                {order?.prescriptionFiles?.map((file: string, idx: number) => (
                  <S.PrescriptionImage
                    key={idx}
                    src={`${bucketUrl}/${file}`}
                    onClick={() => window.open(`${bucketUrl}/${file}`)}
                  />
                ))}
                {(!order?.prescriptionFiles ||
                  order.prescriptionFiles.length === 0) && (
                  <Typography styleText="color:#9CA3AF">
                    No prescription files uploaded
                  </Typography>
                )}
              </S.PrescriptionGrid>
            </S.PrescriptionView>
          </S.Card>
        </S.MainContent>

        {/* Right Side: Cart & Invoice */}
        <S.Sidebar>
          <S.Card>
            <Typography styleText="font-weight:600;margin-bottom:15px">
              Current Invoice
            </Typography>
            <S.CartList>
              {cart.map((item) => (
                <S.CartItem key={item.id}>
                  <div style={{ flex: 1 }}>
                    <Typography styleText="font-weight:500;font-size:14px">
                      {item.name}
                    </Typography>
                    <Typography styleText="font-size:12px;color:#6B7280">
                      रू {item.actualPrice}{" "}
                      {item.isAlternative && <S.Badge>Alt</S.Badge>}
                    </Typography>
                  </div>
                  <S.QuantityControls>
                    <button onClick={() => updateQuantity(item.id, -1)}>
                      -
                    </button>
                    <span>{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, 1)}>
                      +
                    </button>
                  </S.QuantityControls>
                  <S.RemoveButton onClick={() => removeFromCart(item.id)}>
                    <Trash2 size={16} color="#EF4444" />
                  </S.RemoveButton>
                </S.CartItem>
              ))}
              {cart.length === 0 && <S.EmptyCart>Cart is empty</S.EmptyCart>}
            </S.CartList>

            <S.Totals>
              <Row styleRow="justify-content:space-between;margin-bottom:5px">
                <Typography>Subtotal</Typography>
                <Typography styleText="font-weight:600">
                  रू {calculateTotal()}
                </Typography>
              </Row>
              <div style={{ margin: "12px 0" }}>
                <Typography styleText="font-size:14px;font-weight:600;margin-bottom:8px;color:#374151">
                  Custom Discount
                </Typography>
                <Row gap="8px">
                  <button
                    onClick={() => setDiscountType("flat")}
                    style={{
                      flex: 1,
                      padding: "8px",
                      borderRadius: "6px",
                      border: "1px solid #D1D5DB",
                      background: discountType === "flat" ? "#10B981" : "white",
                      color: discountType === "flat" ? "white" : "#374151",
                      cursor: "pointer",
                    }}
                  >
                    Flat
                  </button>
                  <button
                    onClick={() => setDiscountType("percentage")}
                    style={{
                      flex: 1,
                      padding: "8px",
                      borderRadius: "6px",
                      border: "1px solid #D1D5DB",
                      background:
                        discountType === "percentage" ? "#10B981" : "white",
                      color:
                        discountType === "percentage" ? "white" : "#374151",
                      cursor: "pointer",
                    }}
                  >
                    Percent
                  </button>
                </Row>
                <input
                  min="0"
                  max={discountType === "percentage" ? 100 : undefined}
                  type="number"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  placeholder={
                    discountType === "percentage" ? "Enter %" : "Enter amount"
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "8px",
                    border: "1px solid #D1D5DB",
                    fontSize: "14px",
                    marginTop: "8px",
                  }}
                />
              </div>
              {calculateDiscount() > 0 && (
                <Row styleRow="justify-content:space-between;margin-bottom:5px">
                  <Typography>Discount</Typography>
                  <Typography styleText="font-weight:600;color:#EF4444">
                    - रू {calculateDiscount()}
                  </Typography>
                </Row>
              )}
              <Row styleRow="justify-content:space-between;margin-bottom:5px">
                <Typography>Delivery Fee</Typography>
                <Typography styleText="font-weight:600">
                  रू {order?.deliveryCharge || 0}
                </Typography>
              </Row>
              <S.Divider />
              <Row styleRow="justify-content:space-between;margin-top:10px">
                <Typography styleText="font-weight:600;font-size:18px">
                  Total
                </Typography>
                <Typography styleText="font-weight:600;font-size:18px;color:#10B981">
                  रू{" "}
                  {calculateTotal() -
                    calculateDiscount() +
                    (order?.deliveryCharge || 0)}
                </Typography>
              </Row>
            </S.Totals>

            <Button
              onClick={handleSendInvoice}
              disabled={cart.length === 0}
              icon={<Send size={18} style={{ marginRight: "8px" }} />}
              title="Send Invoice"
            />
          </S.Card>
        </S.Sidebar>
      </Row>

      {/* Custom Item Modal */}
      {showCustomModal && (
        <S.ModalOverlay>
          <S.ModalContent>
            <Typography styleText="font-size:18px;font-weight:600;margin-bottom:20px">
              Add Custom Item
            </Typography>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "15px" }}
            >
              <Fields.Input
                name=""
                label="Item Name"
                value={customItem.name}
                onChange={(e: any) =>
                  setCustomItem({ ...customItem, name: e.target.value })
                }
              />
              <Row gap="10px">
                <div style={{ flex: 1 }}>
                  <Fields.Input
                    name=""
                    label="Price (रू)"
                    type="number"
                    value={customItem.price}
                    onChange={(e: any) =>
                      setCustomItem({ ...customItem, price: e.target.value })
                    }
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Fields.Input
                    name=""
                    label="Unit"
                    value={customItem.unit}
                    onChange={(e: any) =>
                      setCustomItem({ ...customItem, unit: e.target.value })
                    }
                  />
                </div>
              </Row>
              <Fields.Switch
                label="Is Alternative?"
                name="isAlternative"
                onChange={(val: boolean) =>
                  setCustomItem({ ...customItem, isAlternative: val })
                }
              />
              <Row styleRow="margin-top:10px">
                <Button
                  onClick={() => setShowCustomModal(false)}
                  variant="outline"
                  title="cancel"
                />
                <Button onClick={addCustomToCart} title="Add to Invoice" />
              </Row>
            </div>
          </S.ModalContent>
        </S.ModalOverlay>
      )}
    </S.POSContainer>
  );
};

export default PharmacyPOS;
